import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Divider,
  Chip,
  Alert,
  Fade,
  Slide,
} from "@mui/material";
import {
  Send as SendIcon,
  SmartToy as SmartToyIcon,
  Person as PersonIcon,
  Minimize as MinimizeIcon,
  OpenInFull as OpenInFullIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useChat } from "../hooks/useChat";

const ChatContainer = styled(Paper)<{
  embedded?: boolean;
  isMinimized?: boolean;
}>(({ theme, embedded, isMinimized }) => ({
  position: embedded ? "relative" : "fixed",
  bottom: embedded ? 0 : 20,
  right: embedded ? 0 : 20,
  width: embedded ? "100%" : 400,
  height: isMinimized ? "auto" : embedded ? "100%" : 600,
  maxHeight: embedded ? "none" : "80vh",
  display: "flex",
  flexDirection: "column",
  borderRadius: theme.spacing(2),
  boxShadow: embedded ? theme.shadows[2] : theme.shadows[8],
  overflow: "hidden",
  zIndex: embedded ? 1 : 1300,
  transition: "height 0.3s ease, width 0.3s ease",
  [theme.breakpoints.down("sm")]: {
    width: embedded ? "100%" : isMinimized ? "auto" : "95vw",
    height: isMinimized ? "auto" : embedded ? "100%" : "90vh",
    bottom: embedded ? 0 : 10,
    right: embedded ? 0 : 10,
    left: isMinimized ? "auto" : embedded ? 0 : 10,
    minWidth: isMinimized ? "200px" : "auto",
  },
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(1.5, 2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const ChatBody = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1),
  overflowY: "auto",
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const MessageContainer = styled(Box)<{ isUser: boolean }>(
  ({ theme, isUser }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    flexDirection: isUser ? "row-reverse" : "row",
    marginBottom: theme.spacing(1),
  })
);

const MessageBubble = styled(Paper)<{ isUser: boolean }>(
  ({ theme, isUser }) => ({
    padding: theme.spacing(1, 1.5),
    maxWidth: "75%",
    backgroundColor: isUser
      ? theme.palette.primary.main
      : theme.palette.background.paper,
    color: isUser
      ? theme.palette.primary.contrastText
      : theme.palette.text.primary,
    borderRadius: theme.spacing(2),
    borderTopRightRadius: isUser ? theme.spacing(0.5) : theme.spacing(2),
    borderTopLeftRadius: isUser ? theme.spacing(2) : theme.spacing(0.5),
    wordBreak: "break-word",
  })
);

const ChatFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const MessageInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(3),
    paddingRight: theme.spacing(0.5),
  },
}));

const FloatingChatButton = styled(Paper)(({ theme }) => ({
  position: "fixed",
  bottom: 20,
  right: 20,
  width: 60,
  height: 60,
  borderRadius: "50%",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: theme.shadows[6],
  zIndex: 1300,
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: theme.shadows[8],
    transform: "scale(1.05)",
  },
}));

const DotsLoader = styled(Box)({
  display: "flex",
  gap: "4px",
  "& span": {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "currentColor",
    animation: "dotPulse 1.4s infinite ease-in-out both",
  },
  "& span:nth-of-type(1)": {
    animationDelay: "-0.32s",
  },
  "& span:nth-of-type(2)": {
    animationDelay: "-0.16s",
  },
  "@keyframes dotPulse": {
    "0%, 80%, 100%": {
      opacity: 0.3,
      transform: "scale(0.8)",
    },
    "40%": {
      opacity: 1,
      transform: "scale(1)",
    },
  },
});

// Types
interface ChatWindowProps {
  onClose?: () => void;
  embedded?: boolean;
  initialOpen?: boolean;
  hideFloatingButton?: boolean;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  onClose,
  embedded = false,
  initialOpen = false,
  hideFloatingButton = false,
}) => {
  const [isOpen, setIsOpen] = useState(embedded || initialOpen);
  const [isMinimized, setIsMinimized] = useState(false);
  const [inputText, setInputText] = useState("");
  const [limitWarningShown, setLimitWarningShown] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Update isOpen when initialOpen changes (for global chat control)
  useEffect(() => {
    if (!embedded) {
      setIsOpen(initialOpen);
    }
  }, [initialOpen, embedded]);

  const { messages, isLoading, sendMessage, clearChat, sessionInfo } = useChat({
    onError: (error) => {
      console.error("Chat error:", error);
    },
    onLimitReached: (session) => {
      if (!limitWarningShown) {
        setLimitWarningShown(true);
        console.warn("Daily message limit reached:", session);
      }
    },
  });

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    await sendMessage(inputText);
    setInputText("");
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const handleClose = () => {
    clearChat();
    setIsMinimized(false);
    // if (embedded) {
    //   // In embedded mode, close button should minimize
    //   setIsMinimized(!isMinimized);
    // } else {
    setIsOpen(false);
    onClose?.();
    // }
  };
  if (hideFloatingButton) {
    return null;
  }
  if (!embedded && !isOpen && !hideFloatingButton) {
    return (
      <Slide direction="up" in={!isOpen} mountOnEnter unmountOnExit>
        <FloatingChatButton onClick={() => setIsOpen(true)}>
          <SmartToyIcon />
        </FloatingChatButton>
      </Slide>
    );
  }

  return (
    <Fade in={isOpen}>
      <ChatContainer embedded={embedded} isMinimized={isMinimized}>
        <ChatHeader>
          <Box display="flex" alignItems="center" gap={1}>
            <SmartToyIcon />
            <Typography variant="h6" component="h2">
              Data Assistant
            </Typography>
            <Chip
              label={
                sessionInfo.sessionId
                  ? `Session ${sessionInfo.messageCount || 0}`
                  : "AI"
              }
              size="small"
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "inherit",
                fontSize: "0.7rem",
              }}
            />
          </Box>
          <Box display="flex" alignItems="center">
            <IconButton
              size="small"
              onClick={toggleMinimize}
              sx={{ color: "inherit", mr: 0.5 }}
            >
              {isMinimized ? <OpenInFullIcon /> : <MinimizeIcon />}
            </IconButton>
            {/* <IconButton
              size="small"
              onClick={handleClearChat}
              sx={{ color: "inherit", mr: 0.5 }}
            >
              <RefreshIcon />
            </IconButton> */}
            {!embedded && (
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{ color: "inherit" }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </ChatHeader>

        {!isMinimized && (
          <>
            <ChatBody>
              {messages.map((message) => (
                <MessageContainer key={message.id} isUser={message.isUser}>
                  {!message.isUser && (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        backgroundColor: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <SmartToyIcon sx={{ color: "white", fontSize: 18 }} />
                    </Box>
                  )}
                  {message.isUser && (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        backgroundColor: "grey.300",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <PersonIcon sx={{ color: "grey.600", fontSize: 18 }} />
                    </Box>
                  )}
                  <MessageBubble isUser={message.isUser}>
                    {message.isLoading ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        {/* <CircularProgress size={16} color="inherit" /> */}
                        <DotsLoader>
                          <span />
                          <span />
                          <span />
                        </DotsLoader>
                        {/* <Typography variant="body2">{message.text}</Typography> */}
                      </Box>
                    ) : message.error ? (
                      <Alert severity="error" sx={{ p: 0.5 }}>
                        {message.text}
                      </Alert>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {message.text}
                      </Typography>
                    )}
                  </MessageBubble>
                </MessageContainer>
              ))}
              <div ref={messagesEndRef} />
            </ChatBody>

            <Divider />

            <ChatFooter>
              <MessageInput
                fullWidth
                multiline
                maxRows={4}
                placeholder={
                  sessionInfo.isLimitReached
                    ? "Daily message limit reached"
                    : "Ask about your data..."
                }
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading || sessionInfo.isLimitReached}
                slotProps={{
                  htmlInput: { maxLength: 1000 },
                }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={handleSendMessage}
                      disabled={
                        !inputText.trim() ||
                        isLoading ||
                        sessionInfo.isLimitReached
                      }
                      sx={{
                        backgroundColor: "primary.main",
                        color: "white",
                        width: 36,
                        height: 36,
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                        "&:disabled": {
                          backgroundColor: "grey.300",
                        },
                      }}
                    >
                      {isLoading ? (
                        <DotsLoader>
                          <span />
                          <span />
                          <span />
                        </DotsLoader>
                      ) : (
                        <SendIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  ),
                }}
              />

              {/* Daily limit warning */}
              {sessionInfo.isLimitReached && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Daily message limit reached ({sessionInfo.dailyMessageCount}/
                  {sessionInfo.dailyLimit}). Please try again tomorrow or
                  upgrade your plan.
                </Alert>
              )}

              {/* Session info */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={0.5}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textAlign: "left" }}
                >
                  AI responses may contain errors. Verify important information.
                </Typography>

                {sessionInfo.dailyMessageCount !== undefined &&
                  sessionInfo.dailyLimit !== undefined && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textAlign: "right" }}
                    >
                      {sessionInfo.dailyMessageCount}/{sessionInfo.dailyLimit}{" "}
                      daily messages
                    </Typography>
                  )}
              </Box>
            </ChatFooter>
          </>
        )}
      </ChatContainer>
    </Fade>
  );
};

export default ChatWindow;
